import React, { useEffect, useState, useMemo } from 'react';
import QRCode from 'qrcode';

// ── Shape path generators ──────────────────────────────────────────────

function squarePath(x, y, s) {
  return `M${x},${y}h${s}v${s}h${-s}z`;
}

function roundedPath(x, y, s, r) {
  r = Math.min(r, s / 2);
  return `M${x + r},${y}h${s - 2 * r}a${r},${r} 0 0 1 ${r},${r}v${s - 2 * r}a${r},${r} 0 0 1 ${-r},${r}h${-(s - 2 * r)}a${r},${r} 0 0 1 ${-r},${-r}v${-(s - 2 * r)}a${r},${r} 0 0 1 ${r},${-r}z`;
}

function dotPath(cx, cy, r) {
  return `M${cx},${cy - r}a${r},${r} 0 1 1 0,${2 * r}a${r},${r} 0 1 1 0,${-2 * r}z`;
}

function diamondPath(cx, cy, s) {
  const h = s / 2;
  return `M${cx},${cy - h}L${cx + h},${cy}L${cx},${cy + h}L${cx - h},${cy}z`;
}

function cloverPath(cx, cy, s) {
  const r = s * 0.3;
  const off = s * 0.18;
  return [
    dotPath(cx, cy - off, r),
    dotPath(cx + off, cy, r),
    dotPath(cx, cy + off, r),
    dotPath(cx - off, cy, r),
  ].join('');
}

// ── Eye frame shapes ───────────────────────────────────────────────────

function eyeFrameSquare(x, y, s) {
  const outer = squarePath(x, y, s * 7);
  const inner = squarePath(x + s, y + s, s * 5);
  return outer + inner;
}

function eyeFrameRounded(x, y, s) {
  const S = s * 7;
  const r1 = s * 1.8;
  const outer = roundedPath(x, y, S, r1);
  const Si = s * 5;
  const r2 = s * 1.0;
  const inner = roundedPath(x + s, y + s, Si, r2);
  return outer + inner;
}

function eyeFrameCircle(x, y, s) {
  const cx = x + s * 3.5;
  const cy = y + s * 3.5;
  const R = s * 3.5;
  const r = s * 2.5;
  return dotPath(cx, cy, R) + dotPath(cx, cy, r);
}

function eyeFrameLeaf(x, y, s) {
  const S = s * 7;
  const r1Big = s * 2.5;
  const r1Small = 0;
  const outer = `M${x + r1Big},${y}h${S - r1Big - r1Small}a${r1Small},${r1Small} 0 0 1 ${r1Small},${r1Small}v${S - r1Small - r1Big}a${r1Big},${r1Big} 0 0 1 ${-r1Big},${r1Big}h${-(S - r1Big - r1Small)}a${r1Small},${r1Small} 0 0 1 ${-r1Small},${-r1Small}v${-(S - r1Small - r1Big)}a${r1Big},${r1Big} 0 0 1 ${r1Big},${-r1Big}z`;
  const Si = s * 5;
  const r2Big = s * 1.5;
  const r2Small = 0;
  const inner = `M${x + s + r2Big},${y + s}h${Si - r2Big - r2Small}a${r2Small},${r2Small} 0 0 1 ${r2Small},${r2Small}v${Si - r2Small - r2Big}a${r2Big},${r2Big} 0 0 1 ${-r2Big},${r2Big}h${-(Si - r2Big - r2Small)}a${r2Small},${r2Small} 0 0 1 ${-r2Small},${-r2Small}v${-(Si - r2Small - r2Big)}a${r2Big},${r2Big} 0 0 1 ${r2Big},${-r2Big}z`;
  return outer + inner;
}

// ── Eye ball shapes ────────────────────────────────────────────────────

function eyeBallSquare(x, y, s) {
  return squarePath(x + s * 2, y + s * 2, s * 3);
}

function eyeBallRounded(x, y, s) {
  return roundedPath(x + s * 2, y + s * 2, s * 3, s * 0.8);
}

function eyeBallCircle(x, y, s) {
  return dotPath(x + s * 3.5, y + s * 3.5, s * 1.5);
}

function eyeBallDiamond(x, y, s) {
  return diamondPath(x + s * 3.5, y + s * 3.5, s * 3);
}

// ── Lookup maps ────────────────────────────────────────────────────────

const bodyShapeFns = {
  square: (x, y, s) => squarePath(x, y, s),
  rounded: (x, y, s) => roundedPath(x, y, s, s * 0.35),
  dots: (x, y, s) => dotPath(x + s / 2, y + s / 2, s * 0.42),
  diamond: (x, y, s) => diamondPath(x + s / 2, y + s / 2, s),
  clover: (x, y, s) => cloverPath(x + s / 2, y + s / 2, s),
};

const eyeFrameFns = {
  square: eyeFrameSquare,
  rounded: eyeFrameRounded,
  circle: eyeFrameCircle,
  leaf: eyeFrameLeaf,
};

const eyeBallFns = {
  square: eyeBallSquare,
  rounded: eyeBallRounded,
  circle: eyeBallCircle,
  diamond: eyeBallDiamond,
};

// ── Helpers ────────────────────────────────────────────────────────────

function isFinderModule(row, col, moduleCount) {
  if (row < 7 && col < 7) return true;
  if (row < 7 && col >= moduleCount - 7) return true;
  if (row >= moduleCount - 7 && col < 7) return true;
  return false;
}

function getFinderOrigins(moduleCount) {
  return [
    { row: 0, col: 0 },
    { row: 0, col: moduleCount - 7 },
    { row: moduleCount - 7, col: 0 },
  ];
}

// ── Frame dimensions helper ────────────────────────────────────────────

function getFrameLayout(qrSize, frameType, frameColor) {
  const pad = qrSize * 0.08;      // padding around QR inside frame
  const border = qrSize * 0.025;  // frame stroke width
  const textH = qrSize * 0.14;    // height of text banner
  const arrowZone = qrSize * 0.22; // space for arrow

  switch (frameType) {
    case 'simple': {
      const w = qrSize + pad * 2;
      const h = qrSize + pad * 2;
      const qrX = pad;
      const qrY = pad;
      return { width: w, height: h, qrX, qrY, pad, border, frameColor };
    }
    case 'scanme': {
      const w = qrSize + pad * 2;
      const h = qrSize + pad * 2 + textH;
      const qrX = pad;
      const qrY = pad;
      return { width: w, height: h, qrX, qrY, pad, border, textH, frameColor };
    }
    case 'arrow': {
      const w = qrSize + pad * 2 + arrowZone;
      const h = qrSize + pad * 2;
      const qrX = pad;
      const qrY = pad;
      return { width: w, height: h, qrX, qrY, pad, border, arrowZone, frameColor };
    }
    default: // 'none'
      return { width: qrSize, height: qrSize, qrX: 0, qrY: 0, frameColor };
  }
}

// ── Frame renderers ────────────────────────────────────────────────────

function renderSimpleFrame(layout) {
  const { width, height, border, pad, frameColor } = layout;
  const r = pad * 0.7;
  return (
    <rect
      x={border / 2}
      y={border / 2}
      width={width - border}
      height={height - border}
      rx={r}
      ry={r}
      fill="none"
      stroke={frameColor}
      strokeWidth={border}
    />
  );
}

function renderScanmeFrame(layout, qrSize) {
  const { width, height, border, pad, textH, frameColor } = layout;
  const r = pad * 0.7;
  const bannerY = height - textH;
  const fontSize = textH * 0.52;

  return (
    <>
      {/* Outer frame */}
      <rect
        x={border / 2}
        y={border / 2}
        width={width - border}
        height={height - border}
        rx={r}
        ry={r}
        fill="none"
        stroke={frameColor}
        strokeWidth={border}
      />
      {/* Banner background */}
      <path
        d={`M${border},${bannerY}h${width - border * 2}v${textH - r - border / 2}a${r},${r} 0 0 1 -${r},${r}h${-(width - border * 2 - r * 2)}a${r},${r} 0 0 1 -${r},-${r}z`}
        fill={frameColor}
      />
      {/* SCAN ME text */}
      <text
        x={width / 2}
        y={bannerY + textH / 2 + fontSize * 0.1}
        textAnchor="middle"
        dominantBaseline="middle"
        fill="#ffffff"
        fontFamily="Arial, Helvetica, sans-serif"
        fontWeight="800"
        fontSize={fontSize}
        letterSpacing={fontSize * 0.15}
      >
        SCAN ME
      </text>
    </>
  );
}

function renderArrowFrame(layout, qrSize) {
  const { height, qrX, pad, arrowZone, frameColor } = layout;
  const arrowX = qrX + qrSize + pad * 0.5;
  const midY = height / 2;
  const fontSize = arrowZone * 0.28;

  // Curved arrow pointing left toward QR
  const tipX = arrowX + arrowZone * 0.08;
  const tipY = midY + arrowZone * 0.05;
  const ctrlX = arrowX + arrowZone * 0.7;
  const startY = midY - arrowZone * 0.45;
  const arrowSize = arrowZone * 0.1;

  return (
    <>
      {/* Curved arrow line */}
      <path
        d={`M${arrowX + arrowZone * 0.55},${startY} C${ctrlX},${startY + arrowZone * 0.25} ${ctrlX},${tipY - arrowZone * 0.15} ${tipX + arrowSize * 1.5},${tipY}`}
        fill="none"
        stroke={frameColor}
        strokeWidth={arrowZone * 0.045}
        strokeLinecap="round"
      />
      {/* Arrow head */}
      <polygon
        points={`${tipX},${tipY} ${tipX + arrowSize * 2},${tipY - arrowSize * 1.2} ${tipX + arrowSize * 1.6},${tipY + arrowSize * 1.0}`}
        fill={frameColor}
      />
      {/* SCAN */}
      <text
        x={arrowX + arrowZone * 0.52}
        y={midY + arrowZone * 0.24}
        textAnchor="middle"
        dominantBaseline="middle"
        fill={frameColor}
        fontFamily="Arial, Helvetica, sans-serif"
        fontWeight="800"
        fontSize={fontSize}
        letterSpacing={fontSize * 0.1}
      >
        SCAN
      </text>
      {/* ME */}
      <text
        x={arrowX + arrowZone * 0.52}
        y={midY + arrowZone * 0.24 + fontSize * 1.15}
        textAnchor="middle"
        dominantBaseline="middle"
        fill={frameColor}
        fontFamily="Arial, Helvetica, sans-serif"
        fontWeight="800"
        fontSize={fontSize}
        letterSpacing={fontSize * 0.1}
      >
        ME
      </text>
    </>
  );
}

// ── Main component ─────────────────────────────────────────────────────

export default function QRCodeRenderer({
  value,
  size = 220,
  fgColor = '#1a1a2e',
  gradientEnabled = false,
  gradientColor1 = '#1a1a2e',
  gradientColor2 = '#7c6cf0',
  gradientType = 'linear',
  gradientAngle = 135,
  bodyShape = 'square',
  eyeFrameShape = 'square',
  eyeBallShape = 'square',
  logoSrc = null,
  logoSize = 0.22,
  logoRemoveBg = true,
  logoPadding = 4,
  frameType = 'none',
  frameColor = '#1a1a2e',
  errorCorrectionLevel = 'H',
}) {
  const [matrix, setMatrix] = useState(null);

  useEffect(() => {
    if (!value) { setMatrix(null); return; }
    try {
      const qr = QRCode.create(value, { errorCorrectionLevel });
      setMatrix(qr.modules);
    } catch {
      setMatrix(null);
    }
  }, [value, errorCorrectionLevel]);

  const svgContent = useMemo(() => {
    if (!matrix) return null;
    const moduleCount = matrix.size;
    const cellSize = size / (moduleCount + 2);
    const offset = cellSize;

    const bodyPathParts = [];
    const data = matrix.data;

    for (let row = 0; row < moduleCount; row++) {
      for (let col = 0; col < moduleCount; col++) {
        if (!data[row * moduleCount + col]) continue;
        if (isFinderModule(row, col, moduleCount)) continue;
        const x = offset + col * cellSize;
        const y = offset + row * cellSize;
        const fn = bodyShapeFns[bodyShape] || bodyShapeFns.square;
        bodyPathParts.push(fn(x, y, cellSize));
      }
    }

    const origins = getFinderOrigins(moduleCount);
    const eyeFrameParts = [];
    const eyeBallParts = [];

    origins.forEach(({ row, col }) => {
      const x = offset + col * cellSize;
      const y = offset + row * cellSize;
      const frameFn = eyeFrameFns[eyeFrameShape] || eyeFrameFns.square;
      const ballFn = eyeBallFns[eyeBallShape] || eyeBallFns.square;
      eyeFrameParts.push(frameFn(x, y, cellSize));
      eyeBallParts.push(ballFn(x, y, cellSize));
    });

    return {
      bodyPath: bodyPathParts.join(''),
      eyeFramePath: eyeFrameParts.join(''),
      eyeBallPath: eyeBallParts.join(''),
      moduleCount,
      cellSize,
    };
  }, [matrix, size, bodyShape, eyeFrameShape, eyeBallShape]);

  if (!value || !svgContent) return null;

  const qrSize = size;
  const layout = getFrameLayout(qrSize, frameType, frameColor);
  const { width: totalW, height: totalH, qrX, qrY } = layout;

  const fillId = gradientEnabled ? 'url(#qr-gradient)' : fgColor;

  const rad = ((gradientAngle - 90) * Math.PI) / 180;
  const gx1 = 50 - Math.cos(rad) * 50;
  const gy1 = 50 - Math.sin(rad) * 50;
  const gx2 = 50 + Math.cos(rad) * 50;
  const gy2 = 50 + Math.sin(rad) * 50;

  const logoDisplaySize = qrSize * logoSize;
  const logoOffsetX = qrX + (qrSize - logoDisplaySize) / 2;
  const logoOffsetY = qrY + (qrSize - logoDisplaySize) / 2;
  const logoBgPad = logoPadding;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${totalW} ${totalH}`}
      width={totalW}
      height={totalH}
      shapeRendering="geometricPrecision"
    >
      <defs>
        {gradientEnabled && (
          gradientType === 'radial' ? (
            <radialGradient id="qr-gradient" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor={gradientColor1} />
              <stop offset="100%" stopColor={gradientColor2} />
            </radialGradient>
          ) : (
            <linearGradient
              id="qr-gradient"
              x1={`${gx1}%`} y1={`${gy1}%`}
              x2={`${gx2}%`} y2={`${gy2}%`}
            >
              <stop offset="0%" stopColor={gradientColor1} />
              <stop offset="100%" stopColor={gradientColor2} />
            </linearGradient>
          )
        )}
      </defs>

      {/* Full white background */}
      <rect width={totalW} height={totalH} fill="#ffffff" />

      {/* Frame elements (behind QR) */}
      {frameType === 'simple' && renderSimpleFrame(layout)}
      {frameType === 'scanme' && renderScanmeFrame(layout, qrSize)}
      {frameType === 'arrow' && renderArrowFrame(layout, qrSize)}

      {/* QR code group offset by frame padding */}
      <g transform={`translate(${qrX},${qrY})`}>
        {/* Body modules */}
        <path d={svgContent.bodyPath} fill={fillId} />

        {/* Eye frames */}
        <path d={svgContent.eyeFramePath} fill={fillId} fillRule="evenodd" />

        {/* Eye balls */}
        <path d={svgContent.eyeBallPath} fill={fillId} />
      </g>

      {/* Logo */}
      {logoSrc && (
        <>
          {logoRemoveBg && (
            <rect
              x={logoOffsetX - logoBgPad}
              y={logoOffsetY - logoBgPad}
              width={logoDisplaySize + logoBgPad * 2}
              height={logoDisplaySize + logoBgPad * 2}
              rx={logoBgPad}
              ry={logoBgPad}
              fill="#ffffff"
            />
          )}
          <image
            href={logoSrc}
            x={logoOffsetX}
            y={logoOffsetY}
            width={logoDisplaySize}
            height={logoDisplaySize}
            preserveAspectRatio="xMidYMid meet"
          />
        </>
      )}
    </svg>
  );
}
